import { BASE } from './elowenClient';
import { authGeneration, impersonatingAs } from './token';

export type PushDeviceState = 'unsupported-ios-tab' | 'ios-update-required' | 'unsupported' | 'denied' | 'off' | 'on' | 'needs-reenable';
export const PUSH_DEVICE_CHANGED = 'elowen:push-device-changed';

let reenable: { generation: number; registration: ServiceWorkerRegistration; key: Uint8Array<ArrayBuffer> } | undefined;
let sync: { generation: number; pending: Promise<void>; running: boolean } | undefined;
/** A start-up registration that failed for a reason other than a missing gesture. Until a later
 *  registration succeeds, the browser's local subscription is not proof that the daemon can reach it. */
let failure: { generation: number; error: unknown } | undefined;

function unsupportedState(): PushDeviceState | null {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return 'unsupported';
  if (typeof window.PushManager === 'undefined') {
    const ios = ['iPhone', 'iPad', 'iPod'].some(name => navigator.userAgent.includes(name))
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (ios) {
      const standalone = window.matchMedia('(display-mode: standalone)').matches
        || (navigator as Navigator & { standalone?: boolean }).standalone === true;
      return standalone ? 'ios-update-required' : 'unsupported-ios-tab';
    }
  }
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window ? null : 'unsupported';
}

/** Read the browser's actual subscription; a failed browser read is an error, never "off". */
export async function getPushDeviceState(): Promise<PushDeviceState> {
  const unsupported = unsupportedState();
  if (unsupported) return unsupported;
  if (Notification.permission === 'denied') return 'denied';
  const registration = await navigator.serviceWorker.getRegistration('/sw.js');
  const active = sync;
  if (active?.generation === authGeneration() && active.running) {
    try { await active.pending; }
    catch (error) {
      // The gesture refusal has its own actionable device state; other failures remain errors.
      if (reenable?.generation !== authGeneration()) throw error;
    }
  }
  if (window.Notification.permission === 'denied') return 'denied';
  if (reenable?.generation === authGeneration()) return 'needs-reenable';
  if (failure?.generation === authGeneration()) throw failure.error;
  const subscription = await registration?.pushManager.getSubscription();
  return subscription && Notification.permission === 'granted' ? 'on' : 'off';
}

function changed(): void {
  window.dispatchEvent(new Event(PUSH_DEVICE_CHANGED));
}

function assertSession(generation: number): void {
  if (generation !== authGeneration() || impersonatingAs()) throw new Error('Push session changed');
}

/** Convert the standard base64url VAPID key into the bytes PushManager expects. */
function keyBytes(base64: string): Uint8Array<ArrayBuffer> {
  const raw = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, char => char.charCodeAt(0));
}

async function publicKey(): Promise<Uint8Array<ArrayBuffer>> {
  const response = await fetch(`${BASE}/push/vapid-public-key`, { credentials: 'same-origin' });
  if (!response.ok) throw new Error(`VAPID key request failed: ${response.status}`);
  const { publicKey } = await response.json() as { publicKey: string };
  return keyBytes(publicKey);
}

async function upsert(subscription: PushSubscription, generation: number): Promise<void> {
  assertSession(generation);
  const response = await fetch(`${BASE}/push/subscribe`, {
    method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' },
    body: JSON.stringify(subscription.toJSON()),
  });
  if (!response.ok) throw new Error(`subscribe failed: ${response.status}`);
  failure = undefined;
}

/** A permission request is started in the click frame, before registration or network awaits. */
export async function enablePush(): Promise<void> {
  const generation = authGeneration();
  assertSession(generation);
  if (unsupportedState()) throw new Error('Push is unsupported');
  if (reenable?.generation === generation) {
    // Reuse the options already read by sync so subscribe stays inside this user gesture.
    const subscription = await reenable.registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: reenable.key });
    await upsert(subscription, generation);
    reenable = undefined;
    changed();
    return;
  }
  const permission = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (permission !== 'granted') { changed(); return; }
  const registration = await navigator.serviceWorker.register('/sw.js');
  const key = await publicKey();
  assertSession(generation);
  const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  await upsert(subscription, generation);
  reenable = undefined;
  changed();
}

/** Remove the daemon record first, so a refusal leaves the local device available for retry. */
export async function disablePush(): Promise<void> {
  const generation = authGeneration();
  assertSession(generation);
  const registration = await navigator.serviceWorker.getRegistration('/sw.js');
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return;
  assertSession(generation);
  const response = await fetch(`${BASE}/push/unsubscribe`, {
    method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ endpoint: subscription.endpoint }),
  });
  if (!response.ok) throw new Error(`unsubscribe failed: ${response.status}`);
  assertSession(generation);
  if (!await subscription.unsubscribe()) throw new Error('Push unsubscribe failed');
  reenable = undefined;
  failure = undefined;
  changed();
}

/** One authenticated app-start upsert per auth generation, including React's remounts. */
export function syncPushSubscription(): Promise<void> {
  if (impersonatingAs()) return Promise.resolve();
  const generation = authGeneration();
  if (sync?.generation === generation) return sync.pending;
  const pending = (async () => {
    if (!('serviceWorker' in navigator)) return;
    const registration = await navigator.serviceWorker.register('/sw.js');
    if (unsupportedState() || Notification.permission !== 'granted') return;
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;
    const key = await publicKey();
    assertSession(generation);
    const existingKey = subscription.options.applicationServerKey;
    if (!existingKey || !new Uint8Array(existingKey).every((byte, index) => byte === key[index]) || existingKey.byteLength !== key.byteLength) {
      if (!await subscription.unsubscribe()) throw new Error('Push unsubscribe failed');
      assertSession(generation);
      try {
        subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
      } catch (error) {
        if (error instanceof DOMException && error.name === 'NotAllowedError') reenable = { generation, registration, key };
        throw error;
      }
    }
    await upsert(subscription, generation);
    reenable = undefined;
  })().catch((error: unknown) => {
    if (reenable?.generation !== generation) failure = { generation, error };
    throw error;
  }).finally(() => {
    if (sync?.generation === generation) sync.running = false;
    changed();
  });
  sync = { generation, pending, running: true };
  return pending;
}

/** The device row's retry after a failed start-up registration: register again in this generation. */
export function retryPushSubscriptionSync(): Promise<void> {
  if (failure?.generation === authGeneration()) sync = undefined;
  return syncPushSubscription();
}
